/**
 * Search (THEME.md §7.19; D-139 SR1–SR12, T-229; T-235; T-236): the element of `sections/okt-search.liquid`, in the
 * Overlays group. It is Horizon's dialog (it extends `DialogComponent`, so the scroll lock, the closing animation and
 * the focus's return are Horizon's) and keeps Horizon's id `search-modal`, so the header's button opens it.
 *
 * - Display: `data-shown` takes Display from 750 px and Mobile display below it (popup, sheet, drawer_right,
 *   drawer_left, full); the top sheet starts at the header's lower edge while the header shows (`data-keep-header`).
 * - A press outside closes it (T-236): a pointer press, down and up, outside the search, on its backdrop or on the
 *   header kept in view; a press that starts inside never does (a drag out of the field ends inside it, for Horizon's
 *   click too), nor one on the header's search button, whose click closes it again.
 * - Under a header kept in view (`data-okt-live`, T-236) the top sheet opens as a plain dialog (`show()`, not
 *   `showModal()`), so the header stays in use: its links go, its buttons work and its menus open over the sheet. The
 *   page below is made inert and dimmed by the scrim (a modal dialog's backdrop doesn't exist here), and Escape closes
 *   the search from the header too. The keyboard's focus stays in the search, as in a modal dialog (T-102): Tab from
 *   its last stop goes to its first and Shift+Tab from its first to its last; the header stays in use with a pointer.
 *   Every other display stays modal.
 * - Phones (T-242, T-261): the page behind never scrolls. Horizon's scroll lock holds the page, and where the window
 *   scrolls (below 990 px) the page is pinned where it is too (`data-okt-pinned`), as iOS Safari lets a finger move
 *   the page under the lock. The sheet's scrollers keep their overscroll, and a touch drag that nothing in the search
 *   can scroll goes nowhere, decided once the finger has moved past a slop, so a tap is never cancelled. A tap opens
 *   the dialog within the tap, so the field's focus brings up iOS's keyboard. Under the on-screen keyboard the body
 *   gets room equal to what the keyboard covers (`--okt-search-keyboard`: the window's height less the visual
 *   viewport's height and top), measured on the visual viewport's resize only, never on its scroll (following that
 *   made a scroll loop on a real iPhone, T-166), so the last row scrolls up above the keyboard.
 * - Typing: from Start after's characters (`data-min-chars`), 200 ms after the last key, Shopify's predictive search
 *   (`/search/suggest`) renders this section with the types and the largest limit of the Results blocks placed (their
 *   `data-okt-search-request` markers), sold-out products as set (`data-unavailable`); the answer's Results part
 *   replaces the dialog's (Horizon's morph), with the No results zone when nothing is found (T-236). Fewer characters
 *   show the Default zone.
 * - Combobox (SR9): `aria-expanded` follows the results, each result is an option the field names with
 *   `aria-activedescendant` as the arrows move; Enter opens the option reached (else a single result, else the search
 *   page); Tab leaves the field for Clear, the X, the results and View all; Escape clears the field, then closes;
 *   ⌘K and Ctrl+K open and close it. A polite status line says how many results there are. No results' links and
 *   cards aren't options: they are the page's links after the listbox.
 * - The results are one Tab stop (T-235): one option takes the focus (the first, or the last one the arrows reached)
 *   and the arrows, Home and End move it inside; every other link and button in them is out of the Tab order, so View
 *   all is 4 stops from the field (Clear, the X, the results, View all).
 * - Default (T-235; T-236; T-248): the page carries only the shell (`data-okt-lazy`). Once the page has loaded and
 *   gone idle, or earlier with the pointer on or the focus in the header's search button, or at the opening, the
 *   script asks the Section Rendering API for this section once: the recommendations endpoint for the product this
 *   browser viewed last (Horizon's list), whose render fills Products › Recommendations, else the search page. Blocks
 *   that show the viewed products (`data-okt-search-viewed`) come from a second answer, `/search?q=id:…`, only when the
 *   zone has them. If the search opens before the answer, the zone's room is kept (its last known height, else the
 *   CSS default), so the sheet or pop-up opens at its full height in one move, and the zone fades in when it arrives.
 * - Theme editor (SR11): the Default zone is in the page; once the page is idle the viewed products and the
 *   recommendations come in for its blocks. It opens while the section or one of its blocks is selected: Results with
 *   Sample search's term (`data-sample`) for the section and the Results zone's blocks, the Default zone for its
 *   blocks, and No results, with a search that finds nothing, for its blocks.
 */
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import {
  debounce,
  isPointWithinElement,
  prefersReducedMotion,
  requestIdleCallback,
  yieldToMainThread,
} from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { scrollContainerMediaQuery } from '@theme/scroll-container';

const desktop = matchMedia('(min-width: 750px)');
const OPTIONS = '[data-okt-option], .product-card__link, .resource-card__link';
const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';
/** What Tab can stop on, before the checks for the Tab order, disabled, inert and shown (T-102) */
const TABBABLE = `${FOCUSABLE}, area[href], summary, iframe, audio[controls], video[controls], [contenteditable]`;
/** The header's search button (`#search-modal/showDialog`) */
const OPENER = '[on\\:click^="#search-modal/"]';
/** Theme editor: a search that finds nothing on this store (21 Oct), for the No results zone's preview */
const NO_MATCH = 'zzqxv';
/** The Default zone's last known height for a display at a window width, kept in this browser (T-248) */
const ROOM_KEY = 'okt-search-room';
/** How far a finger moves from where it touched, in px, before its drag is decided: under it, it's still a tap (T-261) */
const SLOP = 10;

/**
 * Sets a custom property in an element's inline style ('' removes it), only when it changes: on these pages a change
 * to an inline style restyles the whole page (:has() rules), which held up the opening (T-248)
 * @param {HTMLElement} element
 * @param {string} name
 * @param {string} value
 */
function setStyle(element, name, value) {
  if (element.style.getPropertyValue(name) === value) return;
  if (value) element.style.setProperty(name, value);
  else element.style.removeProperty(name);
}

/** Theme editor: the zone each selected Search section shows, so a section rendered again shows it again */
const selected = new Map();

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLInputElement} input
 * @property {HTMLButtonElement} clear
 * @property {HTMLElement} body
 * @property {HTMLElement} before
 * @property {HTMLElement} results
 * @property {HTMLElement} status
 * @property {HTMLElement} [scrim]
 */
class OktSearch extends DialogComponent {
  requiredRefs = ['dialog', 'input', 'clear', 'body', 'before', 'results', 'status'];

  #listeners = new AbortController();

  /** @type {AbortController | null} */
  #request = null;

  /** @type {HTMLElement[]} */
  #options = [];

  #active = -1;

  /** The option that takes the results' one Tab stop */
  #roving = 0;

  /** The theme editor: the Default zone's recommendations and viewed products asked */
  #picksLoaded = false;

  /** @type {Promise<void> | null} The Default zone's request, once */
  #beforeLoad = null;

  #beforeFailed = false;

  /** What the search asks for: the Results blocks' types and their largest limit, read once from the page */
  #ask = { types: /** @type {string[]} */ ([]), limit: 10 };

  /** Open as a plain dialog under a header kept in view (T-236) */
  #live = false;

  /** @type {HTMLElement[]} The page's parts made inert while the search is open as a plain dialog */
  #inert = [];

  /** @type {AbortController | null} Waits for the end of the opening's motion (T-248) */
  #opened = null;

  /** @type {'outside' | 'inside' | null} Where the current press started */
  #press = null;

  /** @type {{ x: number, y: number, stop?: boolean } | null} The current touch: where it started and, once it has moved
   * past the slop, whether nothing in the search takes it (T-242, T-261) */
  #touch = null;

  /** The last press's pointer type: a tap opens the search within itself (T-261) */
  #pointerType = '';

  /** @type {number | null} The window's scroll position while the page is pinned behind the search (T-261) */
  #pinned = null;

  /** @type {boolean | null} A Tab in the plain dialog that the browser is acting on: whether with Shift (T-102) */
  #tabbing = null;

  /** Horizon's closing, started once: a second call while the search closes would restart its animation */
  #closeOnce = this.closeDialog;

  closeDialog = () => (this.refs.dialog.classList.contains('dialog-closing') ? Promise.resolve() : this.#closeOnce());

  connectedCallback() {
    super.connectedCallback();

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const { input, dialog } = this.refs;

    this.#ask = this.#readAsk();
    input.addEventListener('input', this.#onInput, { signal });
    input.addEventListener('keydown', this.#onKeydown, { signal });
    input.addEventListener('blur', this.#onBlur, { signal });
    this.refs.results.addEventListener('keydown', this.#onResultsKeydown, { signal });
    this.refs.results.addEventListener('focusin', this.#onResultsFocus, { signal });
    this.refs.results.addEventListener('focusout', this.#onResultsFocus, { signal });
    this.refs.before.addEventListener('animationend', this.#onRevealed, { signal });
    dialog.addEventListener('click', this.#onDialogClick, { signal });
    dialog.addEventListener('keydown', this.#onTab, { signal });
    document.addEventListener('focusin', this.#onFocusIn, { signal });
    dialog.addEventListener('touchstart', this.#onTouchStart, { signal, passive: true });
    dialog.addEventListener('touchmove', this.#onTouchMove, { signal, passive: false });
    document.addEventListener('pointerdown', this.#onPointerDown, { signal, capture: true });
    document.addEventListener('pointerup', this.#onPointerUp, { signal, capture: true });
    document.addEventListener('pointercancel', this.#onPointerCancel, { signal, capture: true });
    document.addEventListener('keydown', this.#onShortcut, { signal });
    document.addEventListener('keydown', this.#onPageEscape, { signal });
    this.addEventListener(DialogOpenEvent.eventName, this.#onOpen, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#onClose, { signal });
    desktop.addEventListener('change', this.#onBreakpoint, { signal });
    // The pinned page (T-261): given back as a link or a form leaves the page, and as the page is hidden; pinned again
    // on a return from the back-forward cache with the search still open; given back where the window stops scrolling
    // (from 990 px the page wrapper scrolls)
    window.addEventListener('click', this.#onLeave, { signal });
    window.addEventListener('submit', this.#onLeave, { signal });
    window.addEventListener('pagehide', this.#unpin, { signal, capture: true });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    scrollContainerMediaQuery.addEventListener('change', this.#unpin, { signal });
    this.#setShown();

    // Once the page has loaded and gone idle (T-248): the top sheet's top, so a press seldom changes an inline style;
    // Horizon's scroll lock once, on and off in one task (nothing is drawn between), as Safari spent about 90 ms on
    // its first change on a page, at the first opening; and the Default zone where the page carries only its shell
    this.#whenIdle(() => {
      if (!this.refs.dialog.open) this.#setTop();

      const root = document.documentElement;
      if (!root.hasAttribute('scroll-lock')) {
        root.setAttribute('scroll-lock', '');
        root.removeAttribute('scroll-lock');
      }

      if (this.refs.before.hasAttribute('data-okt-lazy') && this.#loadsEarly) this.#loadBefore();
    });

    if (this.refs.before.hasAttribute('data-okt-lazy')) {
      document.addEventListener('pointerover', this.#onIntent, { signal, passive: true });
      document.addEventListener('focusin', this.#onIntent, { signal });
    } else if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => this.#loadPicks());
    }

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onSelect, { signal });
      document.addEventListener('shopify:block:select', this.#onSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#onDeselect, { signal });
      document.addEventListener('shopify:block:deselect', this.#onDeselect, { signal });

      const zone = selected.get(this.dataset.sectionId);
      if (zone) this.#preview(zone);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners.abort();
    this.#request?.abort();
    this.#search.cancel();
    this.#release();
    window.visualViewport?.removeEventListener('resize', this.#onViewportResize);
  }

  /**
   * Opens the dialog at this width's display; the top sheet under the header. The header's search button, in view
   * above the top sheet, closes it again (T-236).
   * @param {Event} [event] - The opener's click, from Horizon's `on:click`
   */
  showDialog(event) {
    const { dialog } = this.refs;

    if (dialog.open) {
      const target = event?.target;
      if (target instanceof Element && target.closest(OPENER)) this.closeDialog();
      return;
    }

    this.#setShown();
    this.#live = this.dataset.shown === 'sheet' && this.#setTop() > 0;
    this.toggleAttribute('data-okt-live', this.#live);

    // Horizon opens its dialogs modally, which puts everything else out of reach. Under a header kept in view the top
    // sheet opens as a plain dialog instead, so the header stays in use (T-236): its own show() stands in for
    // showModal() on this element, for this opening.
    // A tap opens the dialog within the tap itself (T-261): iOS brings up the keyboard only for a focus given while it
    // handles the tap, and Horizon opens its dialogs in the next frame. Horizon's showDialog() still runs first (it
    // notes the scroll position its closing goes back to, locks the page and tells of the opening in that frame); its
    // own call to showModal() then does nothing, for this opening.
    const tap = this.#isTap(event);
    const open = this.#live ? HTMLDialogElement.prototype.show : HTMLDialogElement.prototype.showModal;
    if (tap) dialog.showModal = () => {};
    else if (this.#live) dialog.showModal = dialog.show;
    else Reflect.deleteProperty(dialog, 'showModal');

    // The Default zone not in yet (a press before the early request's answer): its room is kept, so the sheet or
    // pop-up opens at its full height in one move and the zone fades in when it arrives (T-248)
    if (this.refs.before.hasAttribute('data-okt-lazy')) this.#keepRoom();

    super.showDialog();
    this.#pin();
    if (tap) {
      open.call(dialog);
      // The dialog's focusing steps give the field its focus (autofocus), in this task
      if (document.activeElement !== this.refs.input) this.refs.input.focus();
    }
    this.#loadBefore();
    this.#loadPicks();
  }

  /**
   * Whether an opening is a tap's click: the press was a touch and the click a pointer's (not a key's, nor ⌘K)
   * @param {Event} [event]
   */
  #isTap(event) {
    return event?.type === 'click' && this.#pointerType === 'touch' && /** @type {MouseEvent} */ (event).detail > 0;
  }

  /**
   * Pins the page where it is while the search is open, where the window scrolls (below 990 px): iOS Safari doesn't
   * hold the page under a finger with Horizon's lock (overflow: hidden on the root), so the page rubber-banded and the
   * toolbar moved it (T-261). The page wrapper is fixed at its scroll position (its own top: a custom property on the
   * root restyled the whole page, about 70 ms in WebKit), so the window has nothing to scroll and the page stays where
   * it is on the screen.
   */
  #pin() {
    const wrapper = document.querySelector('.page-wrapper');
    if (this.#pinned !== null || scrollContainerMediaQuery.matches || !(wrapper instanceof HTMLElement)) return;

    this.#pinned = window.scrollY;
    this.#hushScroll();
    wrapper.style.setProperty('top', `${-this.#pinned}px`);
    document.documentElement.setAttribute('data-okt-pinned', '');
  }

  /** Gives the page back at its scroll position, in one task, so nothing is drawn between. On closing it runs after
   * Horizon's own scrollTo(), which finds nothing to scroll while the page is pinned. */
  #unpin = () => {
    const top = this.#pinned;
    if (top === null) return;

    this.#pinned = null;
    this.#hushScroll();
    document.documentElement.removeAttribute('data-okt-pinned');
    document.querySelector('.page-wrapper')?.style.removeProperty('top');
    window.scrollTo({ top, behavior: 'instant' });
  };

  /**
   * Keeps the window's scroll events of a pin or its release from the page's listeners, for the next two frames:
   * nothing on the screen moves, but the header would take the jump to the top and back for a scroll up and down,
   * and hide itself (T-261)
   */
  #hushScroll() {
    /** @param {Event} event */
    const hush = (event) => {
      if (event.target === document) event.stopImmediatePropagation();
    };
    window.addEventListener('scroll', hush, { capture: true });
    requestAnimationFrame(() =>
      requestAnimationFrame(() => window.removeEventListener('scroll', hush, { capture: true }))
    );
  }

  /**
   * A link or a form leaving the page while it's pinned (a result, a pill, a search sent): the page goes back to its
   * scroll position first, as the browser and Horizon note it for the way back (with the pinned page's 0, Chrome came
   * back at the top every time) (T-261)
   * @param {Event} event
   */
  #onLeave = (event) => {
    if (this.#pinned === null || event.defaultPrevented) return;
    if (event.type === 'submit' || (event.target instanceof Element && event.target.closest('a[href]'))) this.#unpin();
  };

  /** Back on a page from the back-forward cache with the search open: the page pinned again (T-261) */
  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted && this.refs.dialog.open) this.#pin();
  };

  /** Empties the field and shows the Default zone (the field's Clear) */
  clear = () => {
    const { input } = this.refs;

    input.value = '';
    this.refs.clear.hidden = true;
    this.#search.cancel();
    this.#showBefore();
    input.focus();
  };

  /** Forgets the viewed products and hides Recently viewed (its Clear) */
  clearRecent = () => {
    RecentlyViewed.clearProducts();

    const block = this.#recentBlock;
    if (block) {
      block.hidden = true;
      block.querySelector('[data-okt-search-recent-cards]')?.replaceChildren();
    }
    this.refs.input.focus();
  };

  get #minChars() {
    return Math.max(1, Number(this.dataset.minChars) || 1);
  }

  /**
   * Whether a press is outside the search: on its backdrop or the scrim, on the header or the page; not inside it,
   * not on the header's search button (its click closes it again), and not in another dialog or pop-over over it
   * (quick add, the cart drawer), which handles its own
   * @param {PointerEvent} event
   */
  #isOutside(event) {
    const { dialog } = this.refs;
    const { target } = event;

    if (!(target instanceof Element)) return false;
    if (target === dialog) return !isPointWithinElement(event.clientX, event.clientY, dialog);
    if (dialog.contains(target) || target.closest(OPENER)) return false;

    return !target.closest('dialog[open], [popover]');
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#press = null;
    this.#pointerType = event.pointerType;
    if (!this.refs.dialog.open || !event.isPrimary || event.button !== 0) return;

    this.#press = this.#isOutside(event) ? 'outside' : 'inside';
  };

  /** A press that started outside and ends outside closes the search */
  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (this.#press !== 'outside' || !event.isPrimary) return;

    this.#press = null;
    if (this.refs.dialog.open && this.#isOutside(event)) this.closeDialog();
  };

  #onPointerCancel = () => {
    if (this.#press === 'outside') this.#press = null;
  };

  /** Horizon closes on a click on the dialog outside its box; after a press that started inside (a drag out of the
   * field) that click stops at the dialog */
  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    if (event.target === this.refs.dialog && this.#press === 'inside') event.stopPropagation();
  };

  /** Escape closes the search from the header too, while it's open as a plain dialog (Horizon's own handler hears
   * only the keys pressed inside it) */
  /** @param {KeyboardEvent} event */
  #onPageEscape = (event) => {
    if (event.key !== 'Escape' || !this.#live || !this.refs.dialog.open || event.defaultPrevented) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.closeDialog();
  };

  /** Under a header kept in view the search is a plain dialog, so Tab would go on into the header and the browser's
   * bar. As in a modal dialog the keyboard's focus stays in the search (T-102): Tab from its last stop goes to its
   * first, Shift+Tab from its first to its last (Option+Tab too, Safari's Tab for links). A pointer still uses the
   * header. */
  /** @param {KeyboardEvent} event */
  #onTab = (event) => {
    if (event.key !== 'Tab' || event.ctrlKey || event.metaKey || event.defaultPrevented) return;
    if (!this.#live || !this.refs.dialog.open) return;

    const stops = this.#tabStops();
    if (stops.length === 0) return;

    const first = stops[0];
    const last = stops[stops.length - 1];
    const active = document.activeElement;
    // From the end (or from a focus that no Tab stop follows) round to the start, and back the other way
    const following = (/** @type {HTMLElement} */ stop) =>
      !!active && !!(active.compareDocumentPosition(stop) & Node.DOCUMENT_POSITION_FOLLOWING);
    const preceding = (/** @type {HTMLElement} */ stop) =>
      !!active && !!(active.compareDocumentPosition(stop) & Node.DOCUMENT_POSITION_PRECEDING);

    let target = null;
    if (event.shiftKey && (active === first || !stops.some(preceding))) target = last;
    else if (!event.shiftKey && (active === last || !stops.some(following))) target = first;

    if (target) {
      event.preventDefault();
      target.focus();
      return;
    }

    // The browser moves the focus itself. Safari's plain Tab passes over links and buttons, so it can leave from
    // before the last stop: a focus that this key takes out of the search comes back to its first stop (Shift: its
    // last), in #onFocusIn
    this.#tabbing = event.shiftKey;
    setTimeout(() => (this.#tabbing = null));
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    const shift = this.#tabbing;
    if (shift === null) return;

    this.#tabbing = null;
    if (!this.#live || !this.refs.dialog.open) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    const stops = this.#tabStops();
    (shift ? stops[stops.length - 1] : stops[0])?.focus();
  };

  /** The search's Tab stops in order: in the Tab order, enabled, not inert and shown */
  #tabStops() {
    return [...this.refs.dialog.querySelectorAll(TABBABLE)].filter(
      (element) =>
        element instanceof HTMLElement &&
        element.tabIndex >= 0 &&
        !element.matches(':disabled') &&
        !element.closest('[inert]') &&
        (element.checkVisibility
          ? element.checkVisibility({ checkVisibilityCSS: true, visibilityProperty: true })
          : element.getClientRects().length > 0)
    );
  }

  /** On opening: the keyboard's room follows the visual viewport's size while the search is open (T-242). Once the
   * opening's motion is over, the Default zone's height is noted, and a plain dialog puts the page below the header
   * out of reach (inert), as a modal dialog does: that restyles most of the page, which held up the opening's first
   * frame (T-248); meanwhile the scrim takes the presses there */
  #onOpen = () => {
    window.visualViewport?.addEventListener('resize', this.#onViewportResize);
    this.#onViewportResize();

    this.#opened?.abort();
    const opened = new AbortController();
    this.#opened = opened;

    const { dialog } = this.refs;
    const done = () => {
      opened.abort();
      if (!dialog.open || dialog.classList.contains('dialog-closing')) return;
      this.#rememberRoom();
      if (this.#live) this.#makeInert();
    };
    const timer = setTimeout(done, 600);
    opened.signal.addEventListener('abort', () => clearTimeout(timer));
    dialog.addEventListener(
      'animationend',
      (event) => {
        if (event.target === dialog) done();
      },
      { signal: opened.signal }
    );
  };

  /** The page below the header, out of reach under a plain dialog */
  #makeInert() {
    for (const part of document.querySelectorAll('.page-wrapper > :not(#header-group)')) {
      if (part instanceof HTMLElement && !part.inert) {
        part.inert = true;
        this.#inert.push(part);
      }
    }
  }

  /** What the on-screen keyboard covers of the search, as room under its content (T-242): the window's height less the
   * visual viewport's height and top. Read on the visual viewport's resize only (the keyboard opening or closing),
   * never on its scroll, and not while the page is zoomed, where the visual viewport is smaller anyway. */
  #onViewportResize = () => {
    const viewport = window.visualViewport;
    let room = 0;
    if (viewport && Math.abs(viewport.scale - 1) < 0.01) {
      room = Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
    }
    setStyle(this, '--okt-search-keyboard', room > 0 ? `${room}px` : '');
  };

  /** @param {TouchEvent} event */
  #onTouchStart = (event) => {
    const touch = event.touches[0];
    this.#touch = event.touches.length === 1 && touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  /**
   * A touch drag goes to the scroller under it that can still move that way (the body, a row of pills or cards, a
   * card's photos); one that nothing in the search can take goes nowhere, so the page behind never moves (T-242).
   * The way is decided once, from the whole move since the touch, when the finger is past the slop: until then
   * nothing is cancelled, as iOS drops a tap's click and a swipe's scroll with a cancelled move, and a real finger's
   * first move nearly always has some up or down in it (T-261). Two fingers (zoom) pass, and so does a sideways move
   * in the field (the caret or a selection).
   * @param {TouchEvent} event
   */
  #onTouchMove = (event) => {
    const gesture = this.#touch;
    const touch = event.touches[0];
    if (!gesture || !touch || event.touches.length > 1) return;

    if (gesture.stop === undefined) {
      const dx = touch.clientX - gesture.x;
      const dy = touch.clientY - gesture.y;
      if (Math.hypot(dx, dy) < SLOP) return;

      const across = Math.abs(dx) > Math.abs(dy);
      gesture.stop = !this.#takes(event.target, across, across ? dx : dy);
    }
    if (gesture.stop && event.cancelable) event.preventDefault();
  };

  /**
   * Whether something in the search takes a drag: the field sideways, else the nearest scroller under the finger that
   * can still move that way
   * @param {EventTarget | null} target
   * @param {boolean} across - Sideways rather than up and down
   * @param {number} delta - The finger's move: positive is right or down
   */
  #takes(target, across, delta) {
    const { dialog } = this.refs;
    if (!(target instanceof Element) || (across && target.closest('input, textarea'))) return true;

    for (let element = target; element && dialog.contains(element); element = element.parentElement) {
      if (this.#canScroll(element, across, delta)) return true;
      if (element === dialog) break;
    }
    return false;
  }

  /**
   * @param {Element} element
   * @param {boolean} across - Sideways rather than up and down
   * @param {number} delta - The finger's move: positive is right or down, which scrolls towards the start
   */
  #canScroll(element, across, delta) {
    const style = getComputedStyle(element);
    if (across) {
      if (!/(auto|scroll)/.test(style.overflowX) || element.scrollWidth <= element.clientWidth + 1) return false;
      const start = Math.abs(element.scrollLeft);
      return delta > 0 ? start > 0 : start + element.clientWidth < element.scrollWidth - 1;
    }
    if (!/(auto|scroll)/.test(style.overflowY) || element.scrollHeight <= element.clientHeight + 1) return false;
    return delta > 0 ? element.scrollTop > 0 : element.scrollTop + element.clientHeight < element.scrollHeight - 1;
  }

  /** Gives the page back: after a plain dialog, and where it was pinned */
  #release() {
    this.#opened?.abort();
    this.#opened = null;
    for (const part of this.#inert) part.inert = false;
    this.#inert = [];
    this.#live = false;
    this.removeAttribute('data-okt-live');
    this.#unpin();
  }

  /** At the other display past 750 px: a plain dialog under the header that would become another display closes */
  #onBreakpoint = () => {
    this.#setShown();
    if (this.#live && this.refs.dialog.open && this.dataset.shown !== 'sheet') this.closeDialog();
  };

  /** The pointer on, or the focus in, the header's search button: the Default zone a moment before the opening, if the
   * early request hasn't asked yet */
  /** @param {Event} event */
  #onIntent = (event) => {
    if (!this.#beforeLoad && event.target instanceof Element && event.target.closest(OPENER)) this.#loadBefore();
  };

  /**
   * Whether the Default zone comes once the page is idle (T-248): one request in the background, so the first opening
   * finds it in place. Not in the theme editor (the zone is in the page there), nor when the browser asks to save
   * data, where the pointer on the button or the opening still ask.
   */
  get #loadsEarly() {
    const connection = /** @type {{ saveData?: boolean } | undefined} */ (Reflect.get(navigator, 'connection'));
    return !window.Shopify?.designMode && !connection?.saveData;
  }

  /**
   * Runs a callback once the page has loaded and gone idle, never while it loads (Safari has no idle callback: a
   * moment after the load)
   * @param {() => void} callback
   */
  #whenIdle(callback) {
    const { signal } = this.#listeners;
    const run = () => {
      if (!signal.aborted) callback();
    };
    const idle = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 3000 });
      else setTimeout(run, 200);
    };

    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true, signal });
  }

  /** This display at this window width, for the Default zone's last known height */
  get #roomKey() {
    return `${this.dataset.shown}@${window.innerWidth}`;
  }

  /** @returns {Record<string, number>} */
  #rooms() {
    try {
      const rooms = JSON.parse(localStorage.getItem(ROOM_KEY) ?? '{}');
      return rooms && typeof rooms === 'object' ? rooms : {};
    } catch {
      return {};
    }
  }

  /** The room the Default zone takes while it's on its way: its last known height here, else the CSS default (the
   * pop-up and the top sheet; drawers and full screen have their height anyway) */
  #keepRoom() {
    const { before } = this.refs;
    const height = Number(this.#rooms()[this.#roomKey]);

    setStyle(before, '--okt-search-room', height > 0 ? `${height}px` : '');
    before.setAttribute('data-okt-room', '');
  }

  /** Notes the Default zone's height while it shows, for the next page's room (a few displays and widths) */
  #rememberRoom() {
    const { dialog, before } = this.refs;
    const zone = before.querySelector('[data-okt-zone]');
    if (!dialog.open || before.hidden || before.hasAttribute('data-okt-lazy') || !zone) return;

    const height = Math.round(zone.getBoundingClientRect().height);
    if (height <= 0) return;

    const key = this.#roomKey;
    const rooms = this.#rooms();
    if (rooms[key] === height) return;

    Reflect.deleteProperty(rooms, key);
    const entries = [...Object.entries(rooms), [key, height]].slice(-4);
    try {
      localStorage.setItem(ROOM_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch {
      // Storage full or blocked: the CSS default stays
    }
  }

  /** The Default zone, once (nothing to do where it's in the page: the theme editor); after a failed request, the
   * next opening asks again */
  #loadBefore() {
    if (!this.refs.before.hasAttribute('data-okt-lazy')) return;
    if (this.#beforeLoad && !this.#beforeFailed) return;

    this.#beforeFailed = false;
    this.#beforeLoad = this.#fetchBefore().catch(() => {
      this.#beforeFailed = true;
    });
  }

  async #fetchBefore() {
    const ids = RecentlyViewed.getProducts();

    // With a viewed product, the recommendations endpoint's render, which fills Products › Recommendations; else
    // (or if it fails) the search page, with the viewed products' ids when there are any. Not from the cache: on the
    // search page the section renderer holds the page's own markup, without the zone
    /** @type {Element | null} */
    let zone = null;
    let viewed = false;
    if (ids.length > 0 && this.dataset.recommendationsUrl) {
      zone = await this.#zoneFrom(this.#recommendationsURL(ids[0])).catch(() => null);
    }
    if (!zone) {
      zone = await this.#zoneFrom(ids.length > 0 ? this.#viewedURL(ids) : new URL(Theme.routes.search_url, location.origin));
      viewed = ids.length > 0;
    }
    if (!zone) throw new Error('Search: no Default zone in the answer');
    if (!viewed) await this.#fillViewed(zone, ids).catch(() => {});

    // While the search opens or closes, its motion ends first, so the zone doesn't hold up a frame of it (its room is
    // kept meanwhile); with the search closed (the early request) the parse and the morph go in separate tasks, so
    // neither is a long one
    const { dialog, before } = this.refs;
    const settle = () => Promise.allSettled(dialog.getAnimations().map((animation) => animation.finished));
    await settle();
    if (dialog.classList.contains('dialog-closing')) await settle();
    if (!dialog.open) await yieldToMainThread();

    const height = dialog.open ? dialog.getBoundingClientRect().height : 0;
    morph(before, zone);
    before.removeAttribute('data-okt-lazy');
    if (dialog.open && !before.hidden) this.#reveal(height);
  }

  /**
   * This section's Default zone as Shopify renders it for a URL (none for a failed request)
   * @param {URL} url
   * @returns {Promise<Element | null>}
   */
  async #zoneFrom(url) {
    const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId ?? '', false, url);
    const zone = new DOMParser().parseFromString(html, 'text/html').querySelector('.okt-search__before');

    return zone && !zone.hasAttribute('data-okt-lazy') ? zone : null;
  }

  /** @param {string} id - The product viewed last */
  #recommendationsURL(id) {
    const url = new URL(this.dataset.recommendationsUrl ?? '', location.origin);
    url.searchParams.set('product_id', id);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', 'related');
    return url;
  }

  /** @param {string[]} ids - The viewed products, newest first */
  #viewedURL(ids) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    return url;
  }

  /**
   * Recently viewed and Products › Recently viewed draw the viewed products from a search for their ids: a second
   * answer, asked only when the zone has such a block
   * @param {Element} zone - The Default zone, parsed from an answer or in the page (the theme editor)
   * @param {string[]} ids
   */
  async #fillViewed(zone, ids) {
    const blocks = [...zone.querySelectorAll('[data-okt-search-viewed]')];
    if (ids.length === 0 || blocks.length === 0) return;

    const fresh = await this.#zoneFrom(this.#viewedURL(ids));
    if (fresh) this.#swapBlocks(blocks, fresh);
  }

  /**
   * Each block takes its namesake (`data-okt-block`) from another answer
   * @param {Element[]} blocks
   * @param {Element} fresh
   */
  #swapBlocks(blocks, fresh) {
    for (const block of blocks) {
      if (!(block instanceof HTMLElement)) continue;

      const next = fresh.querySelector(`[data-okt-block="${CSS.escape(block.dataset.oktBlock ?? '')}"]`);
      if (next) morph(block, next, { ...MORPH_OPTIONS, childrenOnly: false });
    }
  }

  /** The theme editor, where the Default zone is in the page: Products › Recommendations and the blocks of viewed
   * products take their cards once the page is idle or the search opens (the storefront's come with the zone) */
  async #loadPicks() {
    const { before } = this.refs;
    const ids = RecentlyViewed.getProducts();
    if (this.#picksLoaded || before.hasAttribute('data-okt-lazy') || ids.length === 0) return;

    this.#picksLoaded = true;
    try {
      const recommended = [...before.querySelectorAll('[data-okt-search-source="recommendations"]')];
      if (recommended.length > 0 && this.dataset.recommendationsUrl) {
        const fresh = await this.#zoneFrom(this.#recommendationsURL(ids[0]));
        if (fresh) this.#swapBlocks(recommended, fresh);
      }
      await this.#fillViewed(before, ids);
    } catch {
      this.#picksLoaded = false;
    }
  }

  /**
   * The Default zone arrived with the search open: it fades in where its room was kept, so the sheet or pop-up doesn't
   * grow again (T-248). Only a zone taller than its room unrolls the sheet or pop-up on from its old lower edge to the
   * new one (the section's CSS, on the motion tokens); a shorter one keeps the room until the search closes.
   * @param {number} height - The dialog's height before the zone
   */
  #reveal(height) {
    const { dialog, before } = this.refs;
    const grow = Math.round(dialog.getBoundingClientRect().height - height);

    if (height > 0 && grow > 1) {
      setStyle(dialog, '--okt-search-grow', `${grow}px`);
      dialog.setAttribute('data-okt-grow', '');
    }
    before.setAttribute('data-okt-reveal', '');
    this.#rememberRoom();
  }

  /** @param {AnimationEvent} event */
  #onRevealed = (event) => {
    if (event.target === this.refs.before) this.refs.before.removeAttribute('data-okt-reveal');
  };

  get #recentBlock() {
    return /** @type {HTMLElement | null} */ (this.refs.before.querySelector('[data-okt-search-recent]'));
  }

  /** @returns {{ types: string[], limit: number }} */
  #readAsk() {
    const types = new Set();
    let limit = 1;

    for (const marker of this.refs.results.querySelectorAll('[data-okt-search-request]')) {
      if (!(marker instanceof HTMLElement)) continue;
      types.add(marker.dataset.oktSearchRequest);
      limit = Math.max(limit, Number(marker.dataset.oktSearchLimit) || 1);
    }

    return { types: [...types].filter(Boolean), limit: Math.min(limit, 10) };
  }

  #setShown = () => {
    const shown = desktop.matches ? this.dataset.display : this.dataset.displayMobile;
    if (this.dataset.shown !== (shown || 'popup')) this.dataset.shown = shown || 'popup';
  };

  /** The sheet starts at the header's lower edge while it shows, else at the top (set once the page is idle too, so
   * the press only changes it where the header has moved since)
   * @returns {number} The top, in px */
  #setTop() {
    let top = 0;

    if (this.dataset.shown === 'sheet' && this.hasAttribute('data-keep-header')) {
      const header = document.querySelector('#header-component');
      const bottom = header ? header.getBoundingClientRect().bottom : 0;
      if (bottom > 0) top = Math.round(bottom);
    }

    setStyle(this, '--okt-search-top', top > 0 ? `${top}px` : '');
    return top;
  }

  #onInput = () => {
    const { value } = this.refs.input;
    this.refs.clear.hidden = value.length === 0;
    this.#search(value.trim());
  };

  #search = debounce(
    /** @param {string} term */ (term) => {
      if (term.length < this.#minChars) {
        this.#showBefore();
        return;
      }
      this.#fetchResults(term);
    },
    200
  );

  /** @param {string} term */
  async #fetchResults(term) {
    const { types, limit } = this.#ask;
    if (types.length === 0) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', types.join(','));
    url.searchParams.set('resources[limit]', String(limit));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', this.dataset.unavailable || 'last');

    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;

    /** @type {string} */
    let html;
    try {
      html = await sectionRenderer.getSectionHTML(
        this.dataset.sectionId ?? '',
        !window.Shopify?.designMode,
        url,
        request.signal
      );
    } catch (error) {
      if (request.signal.aborted) return;
      throw error;
    }

    if (request.signal.aborted || this.refs.input.value.trim() !== term) return;

    const answer = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-okt-search-answer]');
    if (!answer) return;

    morph(this.refs.results, answer);
    this.#showResults(term);
  }

  /** @param {string} term */
  #showResults(term) {
    const { results, before, input, body } = this.refs;

    before.hidden = true;
    results.hidden = false;
    body.scrollTop = 0;

    // The options are the results (the listbox); No results' links and cards follow it as the page's links
    const listbox = results.querySelector('[role="listbox"]');
    this.#options = listbox ? /** @type {HTMLElement[]} */ ([...listbox.querySelectorAll(OPTIONS)]) : [];
    this.#options.forEach((option, index) => {
      if (!option.id) option.id = `${this.dataset.sectionId}-option-${index}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
    });
    this.#active = -1;
    this.#rove();
    input.removeAttribute('aria-activedescendant');
    input.setAttribute('aria-expanded', String(this.#options.length > 0));
    this.#announce(term);
  }

  /** The results are one Tab stop: the first option takes it; every other link and button in them leaves the Tab
   * order (a click and a screen reader still reach them) */
  #rove() {
    const listbox = this.refs.results.querySelector('[role="listbox"]');
    if (!listbox) return;

    for (const element of listbox.querySelectorAll(FOCUSABLE)) element.setAttribute('tabindex', '-1');
    this.#roving = 0;
    this.#options[0]?.setAttribute('tabindex', '0');
  }

  /** @param {number} index - The option that takes the results' Tab stop */
  #moveRoving(index) {
    if (!this.#options[index]) return;

    this.#options[this.#roving]?.setAttribute('tabindex', '-1');
    this.#roving = index;
    this.#options[index].setAttribute('tabindex', '0');
  }

  /** The keyboard's focus in the results: the arrows (wrapping), Home and End move it from option to option */
  /** @param {KeyboardEvent} event */
  #onResultsKeydown = (event) => {
    const index = this.#options.indexOf(/** @type {HTMLElement} */ (event.target));
    if (index < 0 || event.altKey || event.ctrlKey || event.metaKey) return;

    const count = this.#options.length;
    let next = index;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = (index + 1) % count;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = (index - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#moveRoving(next);
    this.#options[next]?.focus();
  };

  /** aria-selected follows the keyboard's focus in the results, as it follows the highlight from the field */
  /** @param {FocusEvent} event */
  #onResultsFocus = (event) => {
    const option = /** @type {HTMLElement} */ (event.target);
    const index = this.#options.indexOf(option);
    if (index < 0) return;

    option.setAttribute('aria-selected', String(event.type === 'focusin'));
    if (event.type === 'focusin') this.#moveRoving(index);
  };

  /** aria-activedescendant names an option only while the field has the focus */
  #onBlur = () => {
    this.#setActive(-1);
  };

  #showBefore() {
    const { results, before, input, status } = this.refs;

    this.#request?.abort();
    this.#setActive(-1);
    this.#options = [];
    results.hidden = true;
    before.hidden = false;
    input.setAttribute('aria-expanded', 'false');
    status.textContent = '';
  }

  /** The polite status line: how many results, or that there are suggestions, or none */
  /** @param {string} term */
  #announce(term) {
    const { results, status } = this.refs;
    const listbox = results.querySelector('[role="listbox"]') ?? results;
    const found = listbox.querySelectorAll(
      '.product-card__link, .resource-card__link, .okt-search-resources [data-okt-option]'
    ).length;
    const suggestions = listbox.querySelectorAll('.okt-search-terms [data-okt-option]').length;

    let text = this.dataset.noneText ?? '';
    if (found > 0) text = (this.dataset.countText ?? '').replace('[count]', String(found));
    else if (suggestions > 0) text = this.dataset.suggestionsText ?? '';

    status.textContent = text.replace('[query]', term);
  }

  /** @param {number} index */
  #setActive(index) {
    const previous = this.#options[this.#active];
    if (previous) {
      previous.removeAttribute('data-okt-active');
      previous.setAttribute('aria-selected', 'false');
    }

    this.#active = index;
    const option = this.#options[index];
    const { input } = this.refs;

    if (!option) {
      input.removeAttribute('aria-activedescendant');
      return;
    }

    option.setAttribute('data-okt-active', '');
    option.setAttribute('aria-selected', 'true');
    input.setAttribute('aria-activedescendant', option.id);
    this.#moveRoving(index);
    option.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const { input, results } = this.refs;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        const count = this.#options.length;
        if (count === 0 || results.hidden) return;

        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = this.#active < 0 ? (step > 0 ? 0 : count - 1) : (this.#active + step + count) % count;
        this.#setActive(next);
        break;
      }

      case 'Enter': {
        if (event.isComposing) return;

        const option = this.#options[this.#active];
        if (option) {
          event.preventDefault();
          option.click();
          return;
        }

        if (!input.value.trim()) {
          event.preventDefault();
          return;
        }

        const single = results.hidden ? null : results.querySelector('[data-okt-single]');
        if (single instanceof HTMLElement && single.dataset.oktSingle) {
          event.preventDefault();
          this.#unpin();
          window.location.href = single.dataset.oktSingle;
        }
        break;
      }

      case 'Escape': {
        // The first Escape empties the field; with nothing typed Horizon's dialog closes
        if (!input.value) return;

        event.preventDefault();
        event.stopPropagation();
        this.clear();
        break;
      }

      case 'Tab':
        this.#setActive(-1);
        break;
    }
  };

  /** ⌘K and Ctrl+K open and close the search */
  /** @param {KeyboardEvent} event */
  #onShortcut = (event) => {
    if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
    if (event.key?.toLowerCase() !== 'k') return;

    event.preventDefault();
    this.toggleDialog();
  };

  /** Closing empties the field, so the search opens fresh, as Horizon's does, and gives a plain dialog's page back */
  #onClose = () => {
    const { input } = this.refs;

    this.refs.dialog.removeAttribute('data-okt-grow');
    this.refs.before.removeAttribute('data-okt-reveal');
    this.refs.before.removeAttribute('data-okt-room');
    input.value = '';
    this.refs.clear.hidden = true;
    this.#press = null;
    this.#touch = null;
    this.#search.cancel();
    this.#showBefore();
    this.#release();
    window.visualViewport?.removeEventListener('resize', this.#onViewportResize);
    setStyle(this, '--okt-search-keyboard', '');
  };

  /** @param {Event} event */
  #onSelect = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.sectionId !== this.dataset.sectionId) return;

    const target = event.target instanceof Element ? event.target : null;
    const zoneElement = event.type === 'shopify:block:select' ? target?.closest('[data-okt-zone]') : null;
    const zone = zoneElement instanceof HTMLElement ? zoneElement.dataset.oktZone || 'results' : 'results';
    selected.set(this.dataset.sectionId, zone);
    this.#preview(zone);
  };

  /** @param {Event} event */
  #onDeselect = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.sectionId !== this.dataset.sectionId) return;

    selected.delete(this.dataset.sectionId);
    requestAnimationFrame(() => {
      if (!selected.has(this.dataset.sectionId)) this.closeDialog();
    });
  };

  /** @param {string} zone - 'before', 'results' or 'no_results' */
  #preview(zone) {
    this.showDialog();

    const { input } = this.refs;
    let term = '';
    if (zone === 'results') term = (this.dataset.sample ?? '').trim();
    if (zone === 'no_results') term = NO_MATCH;

    if (term) {
      input.value = term;
      this.refs.clear.hidden = false;
      this.#search.cancel();
      this.#fetchResults(term);
    } else {
      input.value = '';
      this.refs.clear.hidden = true;
      this.#showBefore();
    }
  }
}

if (!customElements.get('okt-search-component')) customElements.define('okt-search-component', OktSearch);
